import {
  GitPullRequest,
  Clock,
  Check,
  X,
  GitMerge,
  GitBranch,
} from "lucide-react";
import type { MergeRequest } from "../../hooks/useMergeRequests";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface MergeRequestListProps {
  mergeRequests: MergeRequest[];
  loading: boolean;
  onSelect: (mr: MergeRequest) => void;
  selectedId?: string;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  targetBranchFilter: string;
  onTargetBranchFilterChange: (branch: string) => void;
  branches?: { id: string; name: string }[];
  /** Optional callback for the "New request" CTA. Hidden when omitted —
   * keeps this component usable from contexts where MR creation isn't
   * possible (e.g. read-only views). The dashboard wires this to open
   * MergePreviewDialog with sensible source/target defaults. */
  onNewRequest?: () => void;
}

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "rejected", label: "Rejected" },
  { value: "merged", label: "Merged" },
  { value: "closed", label: "Closed" },
];

function statusBadge(status: MergeRequest["status"]) {
  const map: Record<
    string,
    { bg: string; text: string; border: string; icon: React.ReactNode; label: string }
  > = {
    pending: {
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
      icon: <Clock className="size-3" />,
      label: "Pending",
    },
    rejected: {
      bg: "bg-red-50",
      text: "text-red-700",
      border: "border-red-200",
      icon: <X className="size-3" />,
      label: "Rejected",
    },
    merged: {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      border: "border-emerald-200",
      icon: <GitMerge className="size-3" />,
      label: "Merged",
    },
    // Withdrawn by the requester (it used to fall through to "Merged").
    closed: {
      bg: "bg-slate-50",
      text: "text-slate-600",
      border: "border-slate-200",
      icon: <X className="size-3" />,
      label: "Closed",
    },
    outdated: {
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
      icon: <Clock className="size-3" />,
      label: "Outdated",
    },
  };
  // "approved" never occurs — approve always triggers merge immediately
  const s = map[status] || map["merged"];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${s.bg} ${s.text} ${s.border}`}
    >
      {s.icon}
      {s.label}
    </span>
  );
}

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function MergeRequestList({
  mergeRequests,
  loading,
  onSelect,
  selectedId,
  statusFilter,
  onStatusFilterChange,
  targetBranchFilter,
  onTargetBranchFilterChange,
  branches,
  onNewRequest,
}: MergeRequestListProps) {
  return (
    <div className="space-y-3">
      {/* Filter controls + create CTA. Filters live on the left so they
          stay where they are; the CTA pins to the right so it's the
          obvious target for anyone landing here looking to create one. */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5">
          <label className="text-[11px] font-medium text-slate-500">Status</label>
          <Select value={statusFilter || "all"} onValueChange={(v) => onStatusFilterChange(v === "all" ? "" : v)}>
            <SelectTrigger size="sm" className="h-7 text-[12px] min-w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value || "all"} value={opt.value || "all"}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-[11px] font-medium text-slate-500">Branch</label>
          <Select value={targetBranchFilter || "all"} onValueChange={(v) => onTargetBranchFilterChange(v === "all" ? "" : v)}>
            <SelectTrigger size="sm" className="h-7 text-[12px] min-w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {branches?.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {onNewRequest && (
          <button
            onClick={onNewRequest}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
          >
            <GitPullRequest className="size-3.5" />
            New request
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-[12px] text-slate-400 text-center py-10">
          Loading merge requests…
        </div>
      ) : mergeRequests.length === 0 ? (
        <div className="text-[12px] text-slate-400 text-center py-10 bg-slate-50 rounded-xl border border-slate-200">
          <GitPullRequest className="size-5 mx-auto mb-2 text-slate-300" />
          No merge requests match the current filters
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Source</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Target</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Requester</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Approver</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Time</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {mergeRequests.map((mr) => {
                const sourceName = mr.sourceBranchName ?? mr.sourceBranchId.slice(0, 8);
                const targetName = mr.targetBranchName ?? mr.targetBranchId.slice(0, 8);
                const requesterName = mr.requestedByName ?? mr.requestedBy.slice(0, 8);
                const approverName = mr.approvedByName;
                const isSelected = selectedId === mr.id;

                return (
                  <tr
                    key={mr.id}
                    onClick={() => onSelect(mr)}
                    className={`cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-indigo-50/60"
                        : "hover:bg-slate-50/50"
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <GitBranch className="size-3.5 text-indigo-400 shrink-0" />
                        <span className="text-sm font-medium text-slate-700">{sourceName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-slate-600">{targetName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {requesterName}
                    </td>
                    <td className="px-4 py-3">
                      {approverName ? (
                        <span className="flex items-center gap-1.5 text-sm text-emerald-600">
                          <Check className="size-3.5 shrink-0" />
                          {approverName}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-400 whitespace-nowrap">
                      {formatRelativeTime(mr.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {mr.strategy === "overwrite" && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-red-50 text-red-600 border border-red-200">
                            overwrite
                          </span>
                        )}
                        {statusBadge(mr.status)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
